import React, { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { auth, db, functions, ENV_SUFFIX } from '../../firebase'
import Button from '../Button'
import TextField from '../Form/TextField'
import { httpsCallable } from 'firebase/functions'
import { doc, updateDoc } from 'firebase/firestore'
import { sendEmail, varifyMfaReset } from '../../hooks/api'
import { toast } from 'react-hot-toast' // ✅ Toast

export default function ResetAuth() {
  const navigate = useNavigate()
  const location = useLocation()
  const { userData } = location.state || {}
  const [step, setStep] = useState(1) // 1: Email input, 2: Code verification
  const [email, setEmail] = useState(userData?.email)
  const [resetCode, setResetCode] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const [userId, setUserId] = useState('')
  const [verificationId, setVerificationId] = useState('')

  const handleSendResetEmail = async (e) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      // This screen is reached from verify-otp, which is signed in, so the
      // callable SDK attaches the caller's ID token automatically and the
      // response carries isAdmin rather than reading as not-found.
      const getUserByEmailFn = httpsCallable(
        functions,
        `getUserByEmail${ENV_SUFFIX}`
      )
      const response = await getUserByEmailFn({ email })

      const userData = response.data
      if (!userData) {
        toast.error('No user found with this email.')
        return
      }

      if (!userData.isTwoFactorEnabled) {
        toast.error(
          'Two-factor authentication is not enabled for this account.'
        )
        return
      }

      setUserId(userData.id)

      const payload = { email }
      const res = await sendEmail(payload)
      if (res.success) {
        setVerificationId(res?.data?.id)
      }

      toast.success('Reset code sent to your email.')
      setStep(2)
    } catch (err) {
      console.error('Reset error:', err)
      toast.error(err.message || 'Failed to send reset code. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleResendCode = async () => {
    setIsResending(true)
    try {
      const payload = {
        email,
        verificationId
      }
      const res = await sendEmail(payload)

      if (res.success) {
        toast.success('Reset code resent to your email.')
      } else {
        toast.error('Failed to resend code.')
      }
    } catch (err) {
      console.error('Resend error:', err)
      toast.error(err.message || 'Failed to resend code. Please try again.')
    } finally {
      setIsResending(false)
    }
  }

  const handleVerifyResetCode = async (e) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      const payload = {
        otp: Number(resetCode),
        id: verificationId
      }
      const response = await varifyMfaReset(payload)

      if (response.success) {
        await updateDoc(doc(db, 'users', userId), {
          isTwoFactorEnabled: false,
          secretKey: null,
          twoFactorRecoveryCodes: [],
          updatedAt: new Date().toISOString()
        })

        toast.success(
          'MFA has been reset successfully. Please set up MFA again.'
        )

        setTimeout(() => {
          navigate('/mfa-setup', {
            state: {
              userData: response?.data,
              userId: response?.data?.userId
            }
          })
        }, 2000)
      } else {
        toast.error('Invalid reset code. Please try again.')
      }
    } catch (err) {
      console.error('Verification error:', err)
      toast.error(
        err.message || 'Failed to verify reset code. Please try again.'
      )
    } finally {
      setIsLoading(false)
    }
  }

  const handleCancel = () => {
    navigate(-1)
  }

  return (
    <div className='login-container'>
      <div className='login-grid'>
        <div className='login-left login-centered'>
          <div className='login-form'>
            <h2 className='text-xl font-bold mb-4'>
              Reset Two-Factor Authentication
            </h2>

            {step === 1 ? (
              <form onSubmit={handleSendResetEmail}>
                <div className='mb-4'>
                  <TextField
                    label='Email'
                    name='email'
                    type='email'
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required={true}
                    autoComplete='off'
                    placeholder='Enter your email'
                  />
                </div>

                <div className='flex items-center gap-4'>
                  <Button
                    onClick={handleCancel}
                    className='login-button !bg-gray-500 !text-white'
                    variant='secondary'>
                    Cancel
                  </Button>
                  <Button
                    type='submit'
                    className='login-button'
                    disabled={isLoading}>
                    {isLoading ? 'Sending...' : 'Send Reset Code'}
                  </Button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleVerifyResetCode}>
                <div className='mb-4'>
                  <p className='mb-2'>
                    Enter the reset code sent to your email:
                  </p>
                  <TextField
                    name='resetCode'
                    type='number'
                    value={resetCode}
                    onChange={(e) => setResetCode(e.target.value)}
                    required={true}
                    autoComplete='off'
                    placeholder='Enter reset code'
                  />
                </div>

                <div className='mb-4'>
                  <button
                    type='button'
                    onClick={handleResendCode}
                    disabled={isResending}
                    className='text-blue-500 hover:underline text-sm'>
                    {isResending ? 'Resending...' : 'Resend code'}
                  </button>
                </div>

                <div className='flex items-center gap-4'>
                  <Button
                    type='submit'
                    className='login-button'
                    disabled={isLoading}>
                    {isLoading ? 'Verifying...' : 'Verify Code'}
                  </Button>
                  <Button
                    onClick={() => setStep(1)}
                    className='login-button'
                    variant='secondary'>
                    Back
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>

        <div className='login-right login-centered login-overflow'>
          <img
            className='login-image'
            src='/assets/images/logo.png'
            alt='Reset MFA visual'
          />
        </div>
      </div>
    </div>
  )
}
